/**
 * ContinuationGuard — 循环检测 + 深度限制。
 *
 * 维护 mount stack（Set<string>），检测到循环立即阻断。
 * 最大嵌套深度 MAX_CONTINUATION_DEPTH = 5。
 */

import type { ContinuationTarget } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import { createContext, useContext, useMemo } from "react";
import { getThemeAccentStyle } from "../../theme/runtime/theme-accents";

export const MAX_CONTINUATION_DEPTH = 5;

/** Mount stack context — 追踪当前已挂载的 target key 集合 */
const MountStackCtx = createContext<Set<string>>(new Set());

/** 为 target 生成唯一 key */
function targetKey(target: ContinuationTarget): string {
    switch (target.kind) {
        case "definition":
            return `definition:${target.id}`;
        case "content":
            return `content:${target.id}`;
        case "for":
            return `for:${target.id}`;
        case "import-document":
            return `import-document:${target.id}`;
        case "fragment":
            return `fragment:${hashCode(target.source)}`;
    }
}

/** 简单字符串哈希 */
function hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const chr = str.charCodeAt(i);
        hash = ((hash << 5) - hash + chr) | 0;
    }
    return hash;
}

/* ── Error 组件 ── */

function RecursionError({ targetKey, stack }: { targetKey: string; stack: Set<string> }) {
    const t = useT("editor");
    return (
        <div
            className="rounded-lg border p-3 text-center theme-accent-banner"
            style={getThemeAccentStyle("error")}
        >
            <div className="text-[11px] font-medium mb-1">
                {t("continuation.recursion")}
            </div>
            <div className="text-[10px] font-mono">
                {targetKey} → {Array.from(stack).join(" → ")} → {targetKey}
            </div>
        </div>
    );
}

function DepthLimitError({ depth }: { depth: number }) {
    const t = useT("editor");
    return (
        <div
            className="rounded-lg border p-3 text-center theme-accent-banner"
            style={getThemeAccentStyle("warning")}
        >
            <div className="text-[11px] font-medium">
                {t("continuation.depthLimit")}
            </div>
            <div className="text-[10px]">
                {t("continuation.depthDetail", {
                    depth,
                    max: MAX_CONTINUATION_DEPTH,
                })}
            </div>
        </div>
    );
}

/* ── ContinuationGuard ── */

/**
 * 包裹续渲内容，提供循环检测和深度限制。
 * 每个 EngineMount resolve 调用前经由此 Guard 保护。
 */
export function ContinuationGuard({
    target,
    children,
}: {
    target: ContinuationTarget;
    children: React.ReactNode;
}) {
    const parentStack = useContext(MountStackCtx);
    const key = targetKey(target);

    // 循环检测
    if (parentStack.has(key)) {
        return <RecursionError targetKey={key} stack={parentStack} />;
    }

    // 深度限制
    if (parentStack.size >= MAX_CONTINUATION_DEPTH) {
        return <DepthLimitError depth={parentStack.size} />;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const newStack = useMemo(
        () => new Set([...parentStack, key]),
        [parentStack, key],
    );

    return (
        <MountStackCtx.Provider value={newStack}>
            {children}
        </MountStackCtx.Provider>
    );
}
